import { useEffect, useMemo, useState } from 'react';
import { formatVoterName } from 'src/utils/format-voter';
import {
  countOf,
  getManualTextTotal,
  getManualTextSent,
  getManualTextSending,
  getManualTextFailed,
  getManualTextCanceled,
  getManualTextProcessed,
  resolveTaskState,
} from 'src/utils/task-helper';
import { useQuery } from '@apollo/client';
import Alert from '@mui/material/Alert';
import Container from '@mui/material/Container';
import Tooltip from '@mui/material/Tooltip';
import { Info as InfoIcon } from '@mui/icons-material';

import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Paper from '@mui/material/Paper';
import { useForm } from 'react-hook-form';
import Typography from '@mui/material/Typography';
import { Helmet } from 'react-helmet-async';
import { format, parseISO } from 'date-fns';
import Breadcrumbs from 'src/components/custom-breadcrumbs';
import FormProvider, { RHFAutocomplete, RHFTextField } from 'src/components/hook-form';
import { GET_CAMPAIGN } from 'src/graphql/campaigns/queries';
import { useParams } from 'src/routes/hook/use-params';
import {
  GET_TASK_SMS_HISTORY,
  GET_TASK_SURVEY_RESPONSES,
  GET_TASK_AUDIO_TASK_HISTORIES,
} from '../../graphql/sms_history/queries';
import {
  GET_TASK,
  GET_TASK_STATE,
  SEARCH_DIRECT_SND_CAMPAIGNS,
} from '../../graphql/tasks/queries';
import StackedProgressBar from 'src/components/progress-bar/stacked-progress-bar';
import { EmailStatusIcon } from 'src/components/iconify/misc';
import { Section } from '../voters/components/Section';
import DataTable from 'src/components/DataTable';
import {
  TwilioStatusBadge,
  getSmsErrorDescription,
} from 'src/components/table/twilio-status-badge';
import { SurveyStatusBadge } from 'src/components/table/survey-status-badge';
import { TwilioCallStatusBadge } from 'src/components/table/twilio-call-status-badge';
import { useConfirmAndSendMessages } from 'src/sections/manager/tasks/components/task/util';
import {
  initiateSurvey,
  sendMessages,
  sendManualMessages,
  initiateVoiceRecordingTask,
} from 'src/api/twilio';
import { sendDirectSndText, sendDirectSndManualTextBatch } from 'src/api/direct_snd';
import { useSnackbar } from 'src/components/snackbar';
import {
  BILLING_SUCCESS_SMS_STATUSES,
  BILLING_SENDING_SMS_STATUSES,
  CANCELED_STATUS,
  FINISHED_SMS_STATUSES,
  ALL_SMS_STATUSES,
  ALL_SURVEY_STATUSES,
  SENT_SURVEY_STATUSES,
  FAILED_SURVEY_STATUSES,
  SUCCESS_CALL_STATUSES,
  FAILED_CALL_STATUSES,
  UNANSWERED_CALL_STATUSES,
  FINISHED_CALL_STATUSES,
  ALL_CALL_STATUES,
  TWILIO_UNSUBSCRIBED_ERROR_CODE,
  DIRECT_SND_UNSUBSCRIBED_ERROR,
  DIRECT_SND_SMS_SUBMITTED_STATUSES,
  FINISHED_DIRECT_SND_SMS_STATUSES,
  DIRECT_SND_SMS_SUCCESS_STATUSES,
  DIRECT_SND_SMS_FAILED_STATUSES,
  CANCELED_DIRECT_SND_SMS_STATUS,
  ORG_SMS_PROVIDERS,
} from 'src/constants/misc';
import { TASK_TYPES } from 'src/constants/task-types';
import { getSmsProviderForSend } from 'src/constants/org-phones';
import { useAuthContext } from 'src/auth/hooks';
import { GET_VOTERS_FOR_SMS_HISTORIES } from 'src/graphql/voters/queries';
import { TEXTING_TASK_STEP_EXECUTION_TYPES } from 'src/constants/task-types';
import Button from 'src/components/Button';
import { useDownloadCSV } from 'src/hooks/useDownloadCSV';
import { ManualNumberTaskRecipients } from 'src/sections/manager/tasks/components/task/manual-number-task-recipients';

const statusOptions = ALL_SMS_STATUSES.map((status) => ({
  label: status,
  value: status,
}));

// Umbrella filter values: "DELIVERED" matches every DirectSND success state (DELIVERED, EXPIRED)
// and "FAILED" every failed one (UNDELIVERABLE, REJECTED, failed).
const DIRECT_SND_DELIVERED_FILTER_VALUE = 'DELIVERED';
const DIRECT_SND_FAILED_FILTER_VALUE = 'FAILED';
const DIRECT_SND_UMBRELLA_FILTERS = {
  [DIRECT_SND_DELIVERED_FILTER_VALUE]: DIRECT_SND_SMS_SUCCESS_STATUSES,
  [DIRECT_SND_FAILED_FILTER_VALUE]: DIRECT_SND_SMS_FAILED_STATUSES,
};

const directSndStatusOptions = [
  ...DIRECT_SND_SMS_SUBMITTED_STATUSES,
  DIRECT_SND_DELIVERED_FILTER_VALUE,
  DIRECT_SND_FAILED_FILTER_VALUE,
  CANCELED_DIRECT_SND_SMS_STATUS,
].map((status) => ({
  label: status.toUpperCase(),
  value: status,
}));

const surveyStatusOptions = ALL_SURVEY_STATUSES.map((status) => ({
  label: status,
  value: status,
}));

const audioTaskHistoryStatusOptions = ALL_CALL_STATUES.map((status) => ({
  label: status,
  value: status,
}));

const returnTaskTypeValue = (taskType, { survey, message, voiceRecordings, defaultValue }) => {
  if (taskType === TASK_TYPES.SMS_SURVEY) {
    return survey;
  }
  if (taskType === TASK_TYPES.MESSAGE) {
    return message;
  }
  if (taskType === TASK_TYPES.VOICE_RECORDING) {
    return voiceRecordings;
  }
  return defaultValue ?? '--';
};

const OrganizationTaskStatus = ({ inCampaigns, organization_id, campaign_id }) => {
  const { user } = useAuthContext();
  const { enqueueSnackbar } = useSnackbar();
  const { downloadResultsCSV } = useDownloadCSV();
  const { confirmRestartTask, confirmCancelTask } = useConfirmAndSendMessages();
  const theme = useTheme();
  const isSuperAdmin = user?.users_user_roles?.[0]?.user_roles_roles?.code === 'superadmin';
  let { orgId, taskId } = useParams();
  orgId = organization_id || orgId;
  const [page, setPage] = useState(1);
  const [taskUpdateType, setTaskUpdateType] = useState('');
  const [updatingTaskStatus, setUpdatingTaskStatus] = useState(false);

  const { data: taskData } = useQuery(GET_TASK, {
    variables: {
      id: taskId,
    },
    skip: !taskId,
  });
  const { data: directSndCampaignData } = useQuery(SEARCH_DIRECT_SND_CAMPAIGNS, {
    variables: {
      where: {
        task_id: { _eq: taskId },
      },
    },
    skip: !taskId || !isSuperAdmin,
  });
  const directSndCampaignId = directSndCampaignData?.rows?.[0]?.direct_snd_campaign_id;

  const campaignId = campaign_id
    ? campaign_id
    : taskData?.row?.tasks_task_campaigns[0].task_campaigns_campaign.campaign_id;

  const taskTypeCode = taskData?.row?.tasks_task_types?.[0]?.task_types_t_type?.code;
  const completeCheckoutSession = taskData?.row?.task_stripe_checkout_sessions?.find(
    (session) => session.payment_status === 'complete'
  );
  const unpaidCheckoutSession = taskData?.row?.task_stripe_checkout_sessions?.find(
    (session) => session.payment_status === 'open' || session.payment_status === 'unpaid'
  );

  const { data: campaign } = useQuery(GET_CAMPAIGN, {
    variables: {
      campaign_id: campaignId,
    },
  });

  const task = taskData?.row || {};
  const { data: taskStateData } = useQuery(GET_TASK_STATE, {
    variables: {
      id: taskId,
    },
    skip: !taskId,
  });
  // Derived across every geography a voting area can carry, not just state_id --
  // a task targeted purely by precinct or county has none, and every query below
  // is gated on this. See src/utils/task-helper.js.
  const state = resolveTaskState(
    taskStateData?.ri_apps_prd_tasks?.[0]?.tasks_task_voting_areas,
    taskStateData?.ri_apps_prd_states
  )?.shortName;

  const { data: smsHistory, loading: smsHistoriesLoading } = useQuery(GET_TASK_SMS_HISTORY, {
    variables: {
      query: {
        task_id: { _eq: taskId },
      },
    },
    skip: !taskId || !state,
  });

  const { data: smsSurveyResponsesData, loading: smsSurveyResponsesLoading } = useQuery(
    GET_TASK_SURVEY_RESPONSES,
    {
      variables: {
        query: {
          task_id: { _eq: taskId },
        },
      },
      skip: !taskId || !state,
    }
  );

  const { data: audioTaskHistoriesData, loading: audioTaskHistoriesLoading } = useQuery(
    GET_TASK_AUDIO_TASK_HISTORIES,
    {
      variables: {
        query: {
          task_id: { _eq: taskId },
        },
      },
      skip: !taskId || !state,
    }
  );

  // Upload-numbers texting tasks have no voters_queries and no sms_history rows -- their
  // recipients and counts live in the linked manual_text_batch aggregates on TaskFragment.
  const isManualNumberTask = !!task?.is_manual_number_task;
  const manualTextBatchId = task?.tasks_manual_text_batches?.[0]?.manual_text_batch_id;
  const totalMessages = isManualNumberTask
    ? getManualTextTotal(task)
    : countOf(task, 'votersTotal');
  const smsHistories = useMemo(() => {
    return smsHistory?.rows.map((eachRow) => ({ ...eachRow })) || [];
  }, [smsHistory]);

  const smsSurveyResponses = useMemo(() => {
    return smsSurveyResponsesData?.rows || [];
  }, [smsSurveyResponsesData]);

  const audioTaskHistories = useMemo(() => {
    return audioTaskHistoriesData?.rows || [];
  }, [audioTaskHistoriesData]);

  const { data: votersDataForMap, loading: votersDataForMapLoading } = useQuery(
    GET_VOTERS_FOR_SMS_HISTORIES,
    {
      variables: {
        where: {
          voter_id: {
            _in: [
              ...smsHistories.map((history) => history.voter_id),
              ...smsSurveyResponses.map((res) => res.voter_id),
              ...audioTaskHistories.map((res) => res.voter_id),
            ],
          },
          state: {
            _eq: state,
          },
        },
      },
      skip:
        (!smsHistories.length || !state) &&
        !smsSurveyResponses.length &&
        !audioTaskHistories.length,
    }
  );

  const isDirectSndTask = useMemo(() => {
    const directSndCount = smsHistories.filter((h) => h.direct_snd_status != null).length;
    const twilioCount = smsHistories.filter((h) => h.status != null).length;
    return directSndCount > twilioCount;
  }, [smsHistories]);

  const votersMapForLookup = useMemo(() => {
    if (!votersDataForMap) {
      return {};
    }
    return votersDataForMap.rows.reduce((acc, voter) => {
      acc[voter.voter_id] = voter;
      return acc;
    }, {});
  }, [votersDataForMap]);

  const defaultValues = {
    statuses: [],
    search: '',
  };

  const methods = useForm({
    defaultValues,
  });

  const updateTaskState = async (type, label) => {
    setTaskUpdateType(type);
    setUpdatingTaskStatus(true);
    try {
      if (type === TEXTING_TASK_STEP_EXECUTION_TYPES.CREATE) {
        const confirm = await confirmRestartTask();
        if (!confirm) {
          setUpdatingTaskStatus(false);
          return;
        }
      }
      if (type === TEXTING_TASK_STEP_EXECUTION_TYPES.CANCEL) {
        const confirm = await confirmCancelTask('messages');
        if (!confirm) {
          setUpdatingTaskStatus(false);
          return;
        }
      }
      if (taskTypeCode === TASK_TYPES.SMS_SURVEY) {
        await initiateSurvey({ taskId, type });
      }
      if (taskTypeCode === TASK_TYPES.MESSAGE) {
        const sendProvider = getSmsProviderForSend(
          campaign?.row?.campaigns_organization?.organization_id,
          task?.tasks_task_phone_numbers?.[0]?.task_phone_numbers_org_phone_number?.purpose_code
        );
        // An upload-numbers task owns a manual_text_batch, and its direct_snd campaign is keyed on
        // manual_text_batch_id with a null task_id. The task-level initiators look that campaign up
        // by task_id and flip sms_history rows by task_id, so for these tasks they match nothing and
        // silently no-op. Drive the batch-level initiators instead.
        if (isManualNumberTask) {
          if (!manualTextBatchId) {
            throw new Error('This task has no linked text batch to update.');
          }
          if (sendProvider === ORG_SMS_PROVIDERS.TWILIO) {
            await sendManualMessages({ manual_text_batch_id: manualTextBatchId, type });
          } else {
            await sendDirectSndManualTextBatch({
              manual_text_batch_id: manualTextBatchId,
              type,
            });
          }
        } else if (sendProvider === ORG_SMS_PROVIDERS.TWILIO) {
          await sendMessages({ taskId, type });
        } else {
          await sendDirectSndText({
            taskId,
            type,
          });
        }
      }
      if (taskTypeCode === TASK_TYPES.VOICE_RECORDING) {
        await initiateVoiceRecordingTask({ taskId, type });
      }
      setUpdatingTaskStatus(false);
      enqueueSnackbar(`Task ${label} initiated succesfully! Please check back in some time.`, {
        variant: 'success',
      });
    } catch (e) {
      setUpdatingTaskStatus(false);
      enqueueSnackbar(e.message, {
        variant: 'error',
      });
    }
  };

  const { statuses, search } = methods.watch();

  // Filtering shrinks the row set, so a stale page number would slice past the end and show an
  // empty table. Keyed on the serialized values because watch() hands back fresh references.
  const statusFilterKey = statuses.map((status) => status.value).join(',');
  useEffect(() => {
    setPage(1);
  }, [statusFilterKey, search]);

  const { smsHistoryPage, filteredSmsHistory } = useMemo(() => {
    let filtered = smsHistories;
    if (!votersDataForMap) {
      return {
        smsHistoryPage: [],
        filteredSmsHistory: [],
      };
    }
    filtered.forEach((history) => {
      history.voter = votersMapForLookup[history.voter_id];
    });
    if (statuses.length) {
      const selectedValues = statuses.flatMap((status) =>
        isDirectSndTask && DIRECT_SND_UMBRELLA_FILTERS[status.value]
          ? DIRECT_SND_UMBRELLA_FILTERS[status.value]
          : status.value
      );
      filtered = filtered.filter((history) =>
        selectedValues.includes(isDirectSndTask ? history.direct_snd_status : history.status)
      );
    }
    if (search.length) {
      filtered = filtered.filter(
        (history) =>
          history.voter.first_name.toLowerCase().includes(search.toLowerCase()) ||
          history.voter.last_name.toLowerCase().includes(search.toLowerCase()) ||
          history.to.toLowerCase().includes(search.toLowerCase())
      );
    }
    const smsHistoryPage = filtered.slice((page - 1) * 10, page * 10);
    return {
      smsHistoryPage,
      filteredSmsHistory: filtered,
    };
  }, [smsHistories, statuses, search, page, votersMapForLookup, votersDataForMap, isDirectSndTask]);

  const { audioTaskHistoryPage, filteredAudioTaskHistory } = useMemo(() => {
    let filtered = audioTaskHistories;
    if (!votersDataForMap) {
      return {
        audioTaskHistoryPage: [],
        filteredAudioTaskHistory: [],
      };
    }
    filtered = filtered.map((history) => ({
      ...history,
      voter: votersMapForLookup[history.voter_id],
    }));
    if (statuses.length) {
      filtered = filtered.filter((history) =>
        statuses.map((status) => status.value).includes(history.status)
      );
    }
    if (search.length) {
      filtered = filtered.filter(
        (history) =>
          history.voter.first_name.toLowerCase().includes(search.toLowerCase()) ||
          history.voter.last_name.toLowerCase().includes(search.toLowerCase()) ||
          history.to.toLowerCase().includes(search.toLowerCase())
      );
    }
    const audioTaskHistoryPage = filtered.slice((page - 1) * 10, page * 10);
    return {
      audioTaskHistoryPage,
      filteredAudioTaskHistory: filtered,
    };
  }, [audioTaskHistories, statuses, search, page, votersMapForLookup, votersDataForMap]);

  const { surveyResponsesPage, filteredSurveyResponses } = useMemo(() => {
    let filtered = [...smsSurveyResponses];
    if (!votersDataForMap) {
      return {
        surveyResponsesPage: [],
        filteredSurveyResponses: [],
      };
    }
    filtered = filtered.map((response) => ({
      ...response,
      voter: votersMapForLookup[response.voter_id],
    }));
    if (statuses.length) {
      filtered = filtered.filter((response) =>
        statuses.map((status) => status.value).includes(response.status)
      );
    }
    if (search.length) {
      filtered = filtered.filter(
        (response) =>
          response.voter.first_name.toLowerCase().includes(search.toLowerCase()) ||
          response.voter.last_name.toLowerCase().includes(search.toLowerCase()) ||
          response.to.toLowerCase().includes(search.toLowerCase())
      );
    }
    const surveyResponsesPage = filtered.slice((page - 1) * 10, page * 10);
    return {
      surveyResponsesPage,
      filteredSurveyResponses: filtered,
    };
  }, [smsSurveyResponses, votersDataForMap, statuses, search, page, votersMapForLookup]);

  // The table is driven by a different row set per task type; resolving all three once keeps the
  // header count, the page slice, the row key and the pager total pointing at the same list.
  const filteredRows = returnTaskTypeValue(taskTypeCode, {
    survey: filteredSurveyResponses,
    message: filteredSmsHistory,
    voiceRecordings: filteredAudioTaskHistory,
    defaultValue: [],
  });
  const rowsPage = returnTaskTypeValue(taskTypeCode, {
    survey: surveyResponsesPage,
    message: smsHistoryPage,
    voiceRecordings: audioTaskHistoryPage,
    defaultValue: [],
  });
  const rowKeyField = returnTaskTypeValue(taskTypeCode, {
    survey: 'sms_survey_response_id',
    message: 'sms_history_id',
    voiceRecordings: 'audio_task_history_id',
    defaultValue: 'sms_history_id',
  });

  const columns = [
    {
      dataField: 'name',
      text: 'Voter Name',
      formatter: (_cell, row) => formatVoterName(row?.voter?.first_name, row?.voter?.last_name),
    },
    {
      dataField: 'to',
      text: 'Voter Mobile',
    },
    ...(taskData?.row?.send_at
      ? [
          {
            dataField: 'send_at',
            text: 'Scheduled At',
            formatter: () => format(parseISO(taskData?.row?.send_at), 'MMM dd, yyyy - hh:mma'),
          },
        ]
      : []),
    {
      dataField: 'status',
      text: 'Status',
      formatter: (cell, row) => {
        if (taskTypeCode === TASK_TYPES.SMS_SURVEY)
          return <SurveyStatusBadge value={cell} twilioErrorCode={row.twilio_error_code} />;
        if (taskTypeCode === TASK_TYPES.MESSAGE)
          return (
            <TwilioStatusBadge
              value={row.direct_snd_status ?? row.status}
              twilioErrorCode={row.twilio_error_code}
              directSndErrorCode={row.direct_snd_error}
            />
          );
        if (taskTypeCode === TASK_TYPES.VOICE_RECORDING)
          return <TwilioCallStatusBadge value={cell} twilioErrorCode={row.twilio_error_code} />;
        return null;
      },
    },
    ...(isSuperAdmin
      ? [
          {
            dataField: '',
            text: 'Text ID',
            formatter: (_, row) => row.sms_sid ?? row.call_sid ?? row.twilio_execution_sid,
          },
        ]
      : []),
  ];

  const finishedMessages = smsHistories.filter((item) =>
    [...FINISHED_SMS_STATUSES, ...FINISHED_DIRECT_SND_SMS_STATUSES].includes(
      item.direct_snd_status ?? item.status
    )
  );
  const sentMessages = smsHistories.filter((item) =>
    [...BILLING_SUCCESS_SMS_STATUSES, ...DIRECT_SND_SMS_SUCCESS_STATUSES].includes(
      item.direct_snd_status ?? item.status
    )
  );
  const sendingMessages = smsHistories.filter((item) =>
    [...BILLING_SENDING_SMS_STATUSES, ...DIRECT_SND_SMS_SUBMITTED_STATUSES].includes(
      item.direct_snd_status ?? item.status
    )
  );
  const failedMessages = smsHistories.filter(
    (item) =>
      ![
        ...BILLING_SUCCESS_SMS_STATUSES,
        ...BILLING_SENDING_SMS_STATUSES,
        ...DIRECT_SND_SMS_SUCCESS_STATUSES,
        ...DIRECT_SND_SMS_SUBMITTED_STATUSES,
      ].includes(item.direct_snd_status ?? item.status)
  );
  const canceledMessages = smsHistories.filter((item) => CANCELED_STATUS === item.status);
  const unsubMessages = smsHistories.filter(
    (item) =>
      item.twilio_error_code === `${TWILIO_UNSUBSCRIBED_ERROR_CODE}` ||
      item.direct_snd_error === DIRECT_SND_UNSUBSCRIBED_ERROR
  );

  const finishedSurveyResponses = smsSurveyResponses.filter(
    (item) => !FAILED_SURVEY_STATUSES.includes(item.status)
  );
  const processedSurveyResponses = smsSurveyResponses.filter((item) =>
    ALL_SURVEY_STATUSES.includes(item.status)
  );
  const sentSurveyResponses = smsSurveyResponses.filter((item) =>
    SENT_SURVEY_STATUSES.includes(item.status)
  );
  const sendingSurveyResponses = smsSurveyResponses.filter(
    (item) => !item.twilio_execution_sid && !item.status
  );
  const failedSurveyResponses = smsSurveyResponses.filter((item) =>
    FAILED_SURVEY_STATUSES.includes(item.status)
  );
  const canceledSurveyResponses = smsSurveyResponses.filter(
    (item) => CANCELED_STATUS === item.status
  );

  const finishedAudioTaskHistories = audioTaskHistories.filter((item) =>
    FINISHED_CALL_STATUSES.includes(item.status)
  );
  const sentAudioTaskHistories = audioTaskHistories.filter((item) =>
    SUCCESS_CALL_STATUSES.includes(item.status)
  );
  const unansweredAudioTaskHistories = audioTaskHistories.filter((item) =>
    UNANSWERED_CALL_STATUSES.includes(item.status)
  );
  const failedAudioTaskHistories = audioTaskHistories.filter((item) =>
    FAILED_CALL_STATUSES.includes(item.status)
  );
  const canceledAudioTaskHistories = audioTaskHistories.filter(
    (item) => CANCELED_STATUS === item.status
  );

  // The header counts are per task type, and for upload-numbers tasks they come from the
  // manual_text_batch aggregates instead of the (always empty) sms_history rows. Resolving
  // them to scalars once keeps the progress bar, the tallies and the percentage in agreement.
  const sentCount = returnTaskTypeValue(taskTypeCode, {
    survey: sentSurveyResponses.length,
    message: isManualNumberTask ? getManualTextSent(task) : sentMessages.length,
    voiceRecordings: sentAudioTaskHistories.length,
    defaultValue: 0,
  });
  const sendingCount = returnTaskTypeValue(taskTypeCode, {
    survey: sendingSurveyResponses.length,
    message: isManualNumberTask ? getManualTextSending(task) : sendingMessages.length,
    voiceRecordings: unansweredAudioTaskHistories.length,
    defaultValue: 0,
  });
  const failedCount = returnTaskTypeValue(taskTypeCode, {
    survey: failedSurveyResponses.length,
    message: isManualNumberTask ? getManualTextFailed(task) : failedMessages.length,
    voiceRecordings: failedAudioTaskHistories.length,
    defaultValue: 0,
  });
  const canceledCount = returnTaskTypeValue(taskTypeCode, {
    survey: canceledSurveyResponses.length,
    message: isManualNumberTask ? getManualTextCanceled(task) : canceledMessages.length,
    voiceRecordings: canceledAudioTaskHistories.length,
    defaultValue: 0,
  });
  const finishedCount = returnTaskTypeValue(taskTypeCode, {
    survey: finishedSurveyResponses.length,
    message: isManualNumberTask ? getManualTextProcessed(task) : finishedMessages.length,
    voiceRecordings: finishedAudioTaskHistories.length,
    defaultValue: 0,
  });

  const statusPercent = totalMessages
    ? Math.round((100 * (sentCount + sendingCount + failedCount)) / totalMessages)
    : 0;

  const handleExportCSV = () => {
    const csvData = filteredRows.map((row) => {
      const addr = [
        row?.voter?.reg_address1,
        row?.voter?.reg_address2,
        row?.voter?.reg_city,
        row?.voter?.state,
        row?.voter?.reg_zip5,
      ]
        .filter(Boolean)
        .join(', ');
      const baseRow = {
        'Voter ID': row?.voter?.voter_id ?? '',
        'Voter Name': formatVoterName(row?.voter?.first_name, row?.voter?.last_name),
        State: row?.voter?.state ?? '',
        Address: addr,
        'Voter Mobile': row.to ?? '',
        Status: row.direct_snd_status ?? row.status ?? '',
        'Error Description':
          getSmsErrorDescription({
            twilioErrorCode: row.twilio_error_code,
            directSndErrorCode: row.direct_snd_error,
          }) ?? '',
        'Updated At': format(parseISO(row.updated_at), 'MMM dd, yyyy - hh:mma'),
      };
      return baseRow;
    });
    const taskTypeLabel = returnTaskTypeValue(taskTypeCode, {
      survey: 'Survey_Responses',
      message: 'SMS_History',
      voiceRecordings: 'Audio_Task_History',
      defaultValue: 'Task_Status',
    });
    downloadResultsCSV(
      `${task?.name || 'Task_Status'}_${taskTypeLabel}_${new Date().getTime()}`,
      csvData
    );
  };

  return (
    <Container>
      <Helmet title="Task Status" />
      <Breadcrumbs
        heading="Task Status"
        links={
          inCampaigns
            ? [
                { name: 'Campaigns Overview', href: '/campaigns' },
                {
                  name: campaign?.row?.name,
                  href: `/campaigns/${campaignId}/${campaign?.campaigns_organization?.organization_id}/edit`,
                },
                { name: 'Task Status' },
              ]
            : [
                { name: 'Organizations Overview', href: '/superadmin/organizations' },
                {
                  name: campaign?.row?.campaigns_organization?.name,
                  href: `/superadmin/organizations/${orgId}/edit`,
                },
                { name: 'Task Message Status' },
              ]
        }
        sx={{ mb: { xs: 3, md: 5 } }}
      />
      <Box mb={2} gap={1} justifyContent="space-between" display="flex" alignItems="flex-start">
        {taskData?.row?.task_stripe_checkout_sessions?.length && !completeCheckoutSession ? (
          <Paper
            elevation={3}
            sx={{
              p: 2,
              backgroundColor: theme.palette.warning.lighter,
            }}
          >
            <Box display="flex" justifyContent="flex-start" alignItems="center" gap={2}>
              <Typography variant="h6">Payment due for this task</Typography>
              <Button onClick={() => window.open(unpaidCheckoutSession?.url, '_self')}>
                Pay now
              </Button>
            </Box>
          </Paper>
        ) : (
          <div />
        )}
        <Box display="flex" flexDirection="row" gap={1} alignItems="center">
          <Button
            onClick={() => updateTaskState(TEXTING_TASK_STEP_EXECUTION_TYPES.CANCEL, 'cancel')}
            color="primary"
            loading={
              taskUpdateType === TEXTING_TASK_STEP_EXECUTION_TYPES.CANCEL && updatingTaskStatus
            }
          >
            Cancel Task
          </Button>
          {/* <Button
          onClick={() =>
            updateTaskState(TEXTING_TASK_STEP_EXECUTION_TYPES.SYNC, 'sync')
          }
          color="info"
          loading={
            taskUpdateType === TEXTING_TASK_STEP_EXECUTION_TYPES.SYNC && updatingTaskStatus
          }
        >
          Sync Task
        </Button> */}
          {statusPercent < 100 ? (
            <Button
              onClick={() => updateTaskState(TEXTING_TASK_STEP_EXECUTION_TYPES.CREATE, 'restart')}
              color="inherit"
              loading={
                taskUpdateType === TEXTING_TASK_STEP_EXECUTION_TYPES.CREATE && updatingTaskStatus
              }
            >
              Restart Task
            </Button>
          ) : null}
        </Box>
      </Box>
      <Section
        title={
          <Box component="span" display="inline-flex" alignItems="center" gap={0.5}>
            {task?.name || ''}
            {isSuperAdmin && directSndCampaignId && (
              <Tooltip title={`DirectSND Campaign ID: ${directSndCampaignId}`}>
                <InfoIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
              </Tooltip>
            )}
          </Box>
        }
      >
        <Alert severity="info" sx={{ mt: 3, ml: 'auto', width: 'fit-content' }}>
          Please refresh your browser to see updated send status and performance
        </Alert>
        <Box mt={3} justifyContent="space-between" gap={3} display="flex">
          <Box display="flex" alignItems="center" gap={2}>
            Send{' '}
            {returnTaskTypeValue(taskTypeCode, {
              survey: 'survey',
              message: 'message',
              voiceRecordings: 'voice recording',
            })}{' '}
            to {totalMessages} voters
          </Box>
          {totalMessages > 0 && finishedCount === totalMessages ? (
            <Box display="flex" alignItems="center" gap={2}>
              Task{' '}
              {returnTaskTypeValue(taskTypeCode, {
                survey: 'surveys',
                message: 'messages',
                voiceRecordings: 'voice recordings',
              })}{' '}
              has been sent.
            </Box>
          ) : (
            <Box display="flex" alignItems="center" gap={2}>
              Estimated completion time : {Math.max(totalMessages - (sentCount + failedCount), 0)}s
            </Box>
          )}
        </Box>
        <Box
          mt={2}
          alignItems="center"
          gap={taskTypeCode === TASK_TYPES.MESSAGE ? 1 : 3}
          display="flex"
          width="100%"
        >
          <StackedProgressBar
            segments={[
              {
                value: (100 * (sentCount + sendingCount)) / (totalMessages || 1),
                color: '#03C03C',
              },
              {
                value: (100 * failedCount) / (totalMessages || 1),
                color: 'red',
              },
            ]}
          />
          <Typography variant="span">{statusPercent}%</Typography>
          {taskTypeCode === TASK_TYPES.MESSAGE ? (
            <Tooltip
              title={`Text messages are delivered directly to cell carriers at the moment they are sent from our system. The status below reflects the carrier’s response on the text delivery to voter. Please note, some carriers do not provide real-time or any delivery confirmations. This means a “pending” or missing status does not mean the message wasn’t received by the voter. Additionally, certain carriers do not support MMS messages, which may affect delivery of images or media.`}
              arrow
            >
              <InfoIcon />
            </Tooltip>
          ) : null}
        </Box>
        <Box mt={5} display="flex" flexDirection="column" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <EmailStatusIcon color="green" />
            Submitted : {sentCount + sendingCount}
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <EmailStatusIcon color="red" />
            Failed : {failedCount}
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <EmailStatusIcon color="grey" />
            Canceled : {canceledCount}
          </Box>
          {/* {taskTypeCode === TASK_TYPES.MESSAGE ? (
            <Box display="flex" alignItems="center" gap={2}>
              <EmailStatusIcon color="orange" />
              Unsubscribed Recipients : {unsubMessages.length}
            </Box>
          ) : null} */}
        </Box>
        {taskData?.row?.send_at ? (
          <Box display="flex" alignItems="center" mt={3} gap={2}>
            <b>Scheduled At:</b>
            {format(parseISO(taskData?.row?.send_at), 'MMM dd, yyyy - hh:mma')}
          </Box>
        ) : null}
      </Section>
      {isManualNumberTask ? (
        <ManualNumberTaskRecipients manualTextBatchId={manualTextBatchId} />
      ) : (
      <Card sx={{ marginTop: 4, paddingTop: 1 }}>
        <FormProvider methods={methods}>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            marginX={1}
            marginY={2}
          >
            <Box display="flex" flexDirection="row" alignItems="center" marginLeft={1.5}>
              <strong>{filteredRows.length}</strong>
              <Box sx={{ color: 'text.secondary', ml: 0.25 }}>
                SMS{filteredRows.length === 1 ? '' : 'es'}
              </Box>
            </Box>
            <Box display="flex" flexDirection="row" gap={2} alignItems="center">
              <Box width={320}>
                <RHFTextField name="search" label="Search" />
              </Box>
              <Box width={320}>
                <RHFAutocomplete
                  multiple
                  name="statuses"
                  label="Select Statuses"
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  options={returnTaskTypeValue(taskTypeCode, {
                    survey: surveyStatusOptions,
                    message: isDirectSndTask ? directSndStatusOptions : statusOptions,
                    voiceRecordings: audioTaskHistoryStatusOptions,
                  })}
                />
              </Box>
              <Button
                size="small"
                onClick={handleExportCSV}
                disabled={
                  smsSurveyResponsesLoading ||
                  smsHistoriesLoading ||
                  audioTaskHistoriesLoading ||
                  votersDataForMapLoading
                }
              >
                Export CSV
              </Button>
            </Box>
          </Box>
        </FormProvider>
        <DataTable
          size="small"
          keyField={rowKeyField}
          loading={
            smsSurveyResponsesLoading ||
            smsHistoriesLoading ||
            audioTaskHistoriesLoading ||
            votersDataForMapLoading
          }
          data={rowsPage}
          columns={columns}
          pagination={{
            page,
            total: filteredRows.length,
          }}
          hidePageSize
          onTableChange={(type, { page: newPage }) => {
            if (type === 'pagination') {
              setPage(newPage);
            }
          }}
          remote
        />
      </Card>
      )}
    </Container>
  );
};

export default OrganizationTaskStatus;
